import { ImporterFeedback, TwinLifecycleStageValues } from '@repo/frontend-types';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { MessageBar } from '@fluentui/react-components';
import {
  Delete20Regular,
  Save20Regular,
  TableAdd20Regular,
  Link20Regular,
  Search20Regular,
  ArrowSync20Regular,
} from '@fluentui/react-icons';
import { HoverTip } from '@repo/ui';
import { ReactComponent as InsertTwinIcon } from '@repo/assets/icons/insert-twin.svg';

// Context
import { useUserStore, useDialogContext, useTwinContext } from '@repo/front-end-state';

// Components
import { Button } from '@repo/design-system';
import { AddFloorplannerFile } from './AddFloorplannerFile';
import { AssetMetadataFileRow } from './AssetMetadataFileRow';
import { AllManufacturerProductsRow } from './AllManufacturerProductsRow';
import ManufacturerProductsAutocomplete from './ManufacturerProductsAutocomplete';

import Config from '../../common/Config';
import { AddSensorInput } from '@repo/backend-types';
import { upsertTwinWithMetadataPreservation } from './upsertTwinWithMetadataPreservation';

import { extractBIDs } from '@repo/front-end-utils';
import { syncSensorsWithFeedback } from './syncSensorsWithFeedback';

import {
  describeRequestError,
  importSpatialHierarchy,
  importManufacturerHierarchy,
  updateTwin,
  versionAndDeleteSensors,
} from '@repo/front-end-services';
interface FMLWorkflowProps {
  className?: string;
}

const FMLWorkflow_: React.FC<FMLWorkflowProps> = ({ className }) => {
  const { authMetadata } = useUserStore();
  const { lastTwin, twinBId, twin, setTwin, setTwinNeedsRefresh, fmlImportFile, setFmlImportFile } =
    useTwinContext();
  const { showDialog, hideDialog } = useDialogContext();
  const [sensorInputs, setSensorInputs] = useState<AddSensorInput[] | null>([]);

  // Floorplanner Importer
  const [importerMode, setImporterMode] = useState<'READ' | 'EDIT'>('READ');

  // Spatial Hierarchy Importer
  // Note: spatialHierarchyMode and manufacturerHierarchyMode are reserved for future use

  // Manufacturer Hierarchy Importer

  const [importerFeedback, setImporterFeedback] = useState<ImporterFeedback[] | undefined>();
  const [assetMetadataFeedback, setAssetMetadataFeedback] = useState<
    ImporterFeedback[] | undefined
  >();
  const [regenerateSheetFeedback, setRegenerateSheetFeedback] = useState<
    ImporterFeedback[] | undefined
  >();
  const [saveToDgraphFeedback6d, setSaveToDgraphFeedback6d] = useState<
    ImporterFeedback[] | undefined
  >();
  const [saveToDgraphFeedback6g, setSaveToDgraphFeedback6g] = useState<
    ImporterFeedback[] | undefined
  >();
  const [spatialHierarchyFeedback, setSpatialHierarchyFeedback] = useState<
    ImporterFeedback[] | undefined
  >();
  const [manufacturerHierarchyFeedback, setManufacturerHierarchyFeedback] = useState<
    ImporterFeedback[] | undefined
  >();

  const [manufacturerProductsFeedback, setManufacturerProductsFeedback] = useState<
    ImporterFeedback[] | undefined
  >();

  const [manufacturerProductsSheetUrl, setManufacturerProductsSheetUrl] = useState<
    string | undefined
  >();

  const [existingTwin, setExistingTwin] = useState(
    Array.isArray(twin?.physicalModel?.children) && twin.physicalModel.children.length > 0,
  );

  const [isSavingSheet, setIsSavingSheet] = useState(false);
  const [isSavingTwin, setIsSavingTwin] = useState(false);

  const getSheetId = useCallback((url?: string) => {
    if (!url) return undefined;
    const match = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : undefined;
  }, []);

  const sheetId = getSheetId(twin?.adminTwin?.google_sheet_url || undefined);

  useEffect(() => {
    if (fmlImportFile.file !== null && importerMode !== 'EDIT') setImporterMode('EDIT');
  }, [fmlImportFile.file, importerMode]);

  const addImporterFeedback = useCallback((feedback: ImporterFeedback, clear?: boolean) => {
    setImporterFeedback(prev => {
      if (clear || !prev) {
        return [feedback];
      }
      return [...prev, feedback];
    });
  }, []);

  const removeImporterFeedback = useCallback((index: number) => {
    setImporterFeedback(prev => {
      if (!prev) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const addAssetMetadataFeedback = useCallback((feedback: ImporterFeedback, clear?: boolean) => {
    setAssetMetadataFeedback(prev => {
      if (clear || !prev) {
        return [feedback];
      }
      return [...prev, feedback];
    });
  }, []);

  const removeAssetMetadataFeedback = useCallback((index: number) => {
    setAssetMetadataFeedback(prev => {
      if (!prev) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const addRegenerateSheetFeedback = useCallback((feedback: ImporterFeedback, clear?: boolean) => {
    setRegenerateSheetFeedback(prev => {
      if (clear || !prev) {
        return [feedback];
      }
      return [...prev, feedback];
    });
  }, []);

  const removeRegenerateSheetFeedback = useCallback((index: number) => {
    setRegenerateSheetFeedback(prev => {
      if (!prev) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const addSaveToDgraphFeedback6d = useCallback((feedback: ImporterFeedback, clear?: boolean) => {
    setSaveToDgraphFeedback6d(prev => {
      if (clear || !prev) {
        return [feedback];
      }
      return [...prev, feedback];
    });
  }, []);

  const removeSaveToDgraphFeedback6d = useCallback((index: number) => {
    setSaveToDgraphFeedback6d(prev => {
      if (!prev) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const addSaveToDgraphFeedback6g = useCallback((feedback: ImporterFeedback, clear?: boolean) => {
    setSaveToDgraphFeedback6g(prev => {
      if (clear || !prev) {
        return [feedback];
      }
      return [...prev, feedback];
    });
  }, []);

  const removeSaveToDgraphFeedback6g = useCallback((index: number) => {
    setSaveToDgraphFeedback6g(prev => {
      if (!prev) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const addSpatialHierarchyFeedback = useCallback((feedback: ImporterFeedback, clear?: boolean) => {
    setSpatialHierarchyFeedback(prev => {
      if (clear || !prev) {
        return [feedback];
      }
      return [...prev, feedback];
    });
  }, []);

  const removeSpatialHierarchyFeedback = useCallback((index: number) => {
    setSpatialHierarchyFeedback(prev => {
      if (!prev) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const addManufacturerHierarchyFeedback = useCallback(
    (feedback: ImporterFeedback, clear?: boolean) => {
      setManufacturerHierarchyFeedback(prev => {
        if (clear || !prev) {
          return [feedback];
        }
        return [...prev, feedback];
      });
    },
    [],
  );

  const removeManufacturerHierarchyFeedback = useCallback((index: number) => {
    setManufacturerHierarchyFeedback(prev => {
      if (!prev) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const addManufacturerProductsFeedback = useCallback(
    (feedback: ImporterFeedback, clear?: boolean) => {
      setManufacturerProductsFeedback(prev => {
        if (clear || !prev) {
          return [feedback];
        }
        return [...prev, feedback];
      });
    },
    [],
  );

  const removeManufacturerProductsFeedback = useCallback((index: number) => {
    setManufacturerProductsFeedback(prev => {
      if (!prev) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const buttonEnabler = useCallback(
    (buttonId: string) => {
      switch (buttonId) {
        case 'import-model':
          const deps: {
            prop: string;
            text: string;
          }[] = [
            { prop: 'latitude', text: 'Latitude' },
            { prop: 'longitude', text: 'Longitude' },
            { prop: 'altitude', text: 'Altitude' },
            { prop: 'orientation', text: 'Orientation' },
          ];

          // Check if values exist in twin.adminTwin
          // Values can be strings or numbers, so we check for truthy values
          const hasValue = (val: unknown): boolean => {
            if (val === null || val === undefined) return false;
            if (typeof val === 'string') return val.trim() !== '';
            if (typeof val === 'number') return !isNaN(val);
            return true;
          };

          const missingDeps = deps.filter(({ prop }) => {
            const value = twin?.adminTwin?.[prop as keyof typeof twin.adminTwin];
            return !hasValue(value);
          });

          if (missingDeps.length > 0) {
            return {
              enabled: false,
              reason: `Needs: ${missingDeps.map(({ text }) => text).join(', ')}`,
            };
          }

          return {
            enabled: true,
            reason: '',
          };
        case 'generate-sheet':
          return {
            enabled: !!twin?.physicalModel && !!twinBId,
            reason: !twin?.physicalModel || !twinBId ? 'No twin model available' : '',
          };
        case 'import-spatial-hierarchy':
          return {
            enabled: !!twin?.physicalModel && !!twinBId && !!authMetadata?.orgBID,
            reason:
              !twin?.physicalModel || !twinBId || !authMetadata?.orgBID
                ? 'Missing twin model, twin ID, or organization ID'
                : '',
          };
        case 'import-manufacturer-hierarchy':
          return {
            enabled: !!twin?.physicalModel && !!twinBId && !!authMetadata?.orgBID,
            reason:
              !twin?.physicalModel || !twinBId || !authMetadata?.orgBID
                ? 'Missing twin model, twin ID, or organization ID'
                : '',
          };
      }
      return {
        enabled: false,
        reason: 'Not implemented',
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `twin` already covers its sub-fields; they are listed for readability
    [twin, twin?.adminTwin, twin?.physicalModel, twinBId, authMetadata?.orgBID],
  );

  const generateMetadataSheet = useCallback(async () => {
    const accessToken: string | undefined = authMetadata?.tokens?.accessToken;

    if (!accessToken) {
      addAssetMetadataFeedback({
        intent: 'error',
        message: 'Missing access token',
      });
      return;
    }

    if (!twinBId) {
      addAssetMetadataFeedback({
        intent: 'error',
        message: 'Missing twin ID',
      });
      return;
    }

    try {
      const response = await fetch(`${Config.middlewareApi}api/google-sheets/generate-sheet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          twin_bid: twinBId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      addAssetMetadataFeedback({
        intent: 'success',
        message: `Twin Metadata File generated successfully! ${result.models_count} models processed.`,
      });

      if (twin?.adminTwin) {
        setTwin({
          ...twin,
          adminTwin: {
            ...twin.adminTwin,
            google_sheet_url: result.sheet_url,
          },
        });
      }

      // Open the Google Sheet in a new tab
      window.open(result.sheet_url, '_blank');
    } catch (error) {
      console.error('Error generating Twin Metadata File:', describeRequestError(error));
      addAssetMetadataFeedback({
        intent: 'error',
        message: 'Failed to generate Twin Metadata File. Please try again.',
      });
    }
  }, [authMetadata, twinBId, addAssetMetadataFeedback, twin, setTwin]);

  const importSpatialHierarchyData = useCallback(async () => {
    const accessToken: string | undefined = authMetadata?.tokens?.accessToken;
    const orgBID: string | undefined = authMetadata?.orgBID;

    if (!accessToken) {
      addSpatialHierarchyFeedback({
        intent: 'error',
        message: 'Missing access token',
      });
      return;
    }

    if (!twinBId) {
      addSpatialHierarchyFeedback({
        intent: 'error',
        message: 'Missing twin ID',
      });
      return;
    }

    if (!orgBID) {
      addSpatialHierarchyFeedback({
        intent: 'error',
        message: 'Missing organisation ID',
      });
      return;
    }

    try {
      const result = await importSpatialHierarchy(Config.middlewareApi, accessToken, {
        twin_bid: twinBId,
        org_bid: orgBID,
      });

      if (result) {
        addSpatialHierarchyFeedback({
          intent: 'success',
          message: `Spatial hierarchy imported successfully! Version: ${result.version}, Entities: ${result.entitiesImported}, Attributes: ${result.attributesImported}`,
        });

        // Display warnings from FML import validation
        // Note: Summary counts (unlinkedSensorCount, entitiesWithoutSensorsCount) are already
        // included in backend-generated warnings via result.warnings, so we only iterate once
        if (result.warnings && result.warnings.length > 0) {
          for (const warning of result.warnings) {
            const affectedCount = warning.affectedBids?.length || 0;
            const affectedList =
              affectedCount > 3
                ? `${warning.affectedBids?.slice(0, 3).join(', ')}... and ${affectedCount - 3} more`
                : warning.affectedBids?.join(', ');

            addSpatialHierarchyFeedback({
              intent: 'warning',
              message: `⚠️ ${warning.message}${affectedList ? ` (${affectedList})` : ''}`,
            });
          }
        }
      }
    } catch (error: any) {
      console.error('Error importing spatial hierarchy:', describeRequestError(error));
      addSpatialHierarchyFeedback({
        intent: 'error',
        message:
          error?.response?.data?.message ||
          `Failed to import spatial hierarchy: ${error?.message || 'Unknown error'}`,
      });
    }
  }, [authMetadata, twinBId, addSpatialHierarchyFeedback]);

  const importManufacturerHierarchyData = useCallback(async () => {
    const accessToken: string | undefined = authMetadata?.tokens?.accessToken;
    const orgBID: string | undefined = authMetadata?.orgBID;

    if (!accessToken) {
      addManufacturerHierarchyFeedback({
        intent: 'error',
        message: 'Missing access token',
      });
      return;
    }

    if (!twinBId) {
      addManufacturerHierarchyFeedback({
        intent: 'error',
        message: 'Missing twin ID',
      });
      return;
    }

    if (!orgBID) {
      addManufacturerHierarchyFeedback({
        intent: 'error',
        message: 'Missing organisation ID',
      });
      return;
    }

    try {
      const result = await importManufacturerHierarchy(Config.middlewareApi, accessToken, {
        twin_bid: twinBId,
        org_bid: orgBID,
      });

      if (result) {
        addManufacturerHierarchyFeedback({
          intent: 'success',
          message: `Manufacturer hierarchy imported successfully! Equipment Mappings: ${result.equipmentMappingsImported}, Membership Attributes: ${result.membershipAttributesImported}`,
        });
      }
    } catch (error: any) {
      console.error('Error importing manufacturer hierarchy:', describeRequestError(error));
      addManufacturerHierarchyFeedback({
        intent: 'error',
        message:
          error?.response?.data?.message ||
          `Failed to import manufacturer hierarchy: ${error?.message || 'Unknown error'}`,
      });
    }
  }, [authMetadata, twinBId, addManufacturerHierarchyFeedback]);

  const handleSaveTwin = useCallback(async () => {
    const accessToken: string | undefined = authMetadata?.tokens?.accessToken;
    const orgBID: string | undefined = authMetadata?.orgBID;

    if (!accessToken) {
      addImporterFeedback({
        intent: 'error',
        message: 'Missing access token',
      });
      return;
    }

    if (!orgBID) {
      addImporterFeedback({
        intent: 'error',
        message: 'Missing orgBID',
      });
      return;
    }

    if (!twin) {
      addImporterFeedback({
        intent: 'error',
        message: 'No twin data available',
      });
      return;
    }

    setIsSavingTwin(true);
    addImporterFeedback(
      {
        intent: 'success',
        message: 'Saving...',
      },
      true,
    );

    try {
      // Read FML file content if available for Azure upload
      let fmlContent: Record<string, unknown> | undefined;
      let fmlFilename: string | undefined;
      if (fmlImportFile.file) {
        try {
          const fileText = await fmlImportFile.file.text();
          fmlContent = JSON.parse(fileText) as Record<string, unknown>;
          fmlFilename = fmlImportFile.file.name;
        } catch (error) {
          console.warn('Failed to read FML file content for Azure upload:', error);
        }
      }

      // Version sensors BEFORE entity creation to enable rollback capability
      // (old sensors are backed up before their entities are versioned/replaced)
      if (lastTwin?.physicalModel && sensorInputs && sensorInputs.length > 0) {
        addImporterFeedback({
          intent: 'success',
          message: `Creating sensor backups for rollback capability...`,
        });

        try {
          // Extract all BIDs from the previous model to match old implementation
          const prevModelBIDs = extractBIDs(lastTwin.physicalModel);

          const sensorDeletionResult = await versionAndDeleteSensors(
            Config.middlewareApi,
            accessToken,
            {
              orgBid: orgBID,
              entityBIDs: prevModelBIDs,
              deletableModelNames: ['PeopleCountLive'],
            },
          );

          if (sensorDeletionResult.success) {
            const versionText = sensorDeletionResult.versionSuffix
              ? ` (Version: ${sensorDeletionResult.versionSuffix})`
              : '';
            addImporterFeedback({
              intent: 'success',
              message: `${sensorDeletionResult.versionedCount} sensors backed up for rollback${versionText}`,
            });
          } else {
            addImporterFeedback({
              intent: 'warning',
              message: `Failed to backup sensors: ${sensorDeletionResult.error}`,
            });
          }
        } catch (error) {
          addImporterFeedback({
            intent: 'error',
            message: `Error backing up sensors: ${error instanceof Error ? error.message : 'Unknown error'}`,
          });
        }
      }

      // First, save entities to Dgraph via upsertTwinWithMetadataPreservation
      // Sensors must be upserted AFTER entities exist so the entity references can be established
      const result = await upsertTwinWithMetadataPreservation({
        accessToken: accessToken,
        orgBID: orgBID,
        tree: twin.physicalModel,
        prevModel: lastTwin?.physicalModel,
        updating: existingTwin,
        twin: twin.adminTwin,
        fmlContent: fmlContent,
        fmlFilename: fmlFilename,
        onMetadataPreservationUpdate: (message, intent = 'info') => {
          // During FML upload, only show essential FML-related messages in importer feedback
          // Don't add any messages to assetMetadataFeedback to avoid cluttering Google Sheet steps
          if (
            message.includes('FML upload') ||
            message.includes('Restoring metadata from backup') ||
            message.includes('Generating fresh Google Sheet')
          ) {
            addImporterFeedback({
              intent,
              message,
            });
          }
          // All other messages (including backup and generation success messages) are silently ignored during FML upload
        },
      });

      if (result.success) {
        addImporterFeedback({
          intent: 'success',
          message: 'Model saved to Dgraph',
        });

        // Upsert sensors AFTER entities exist in Dgraph
        await syncSensorsWithFeedback({
          accessToken,
          orgBID,
          sensorInputs,
          addImporterFeedback,
        });

        // Collect all adminTwin updates to avoid state overwrites
        if (twin?.adminTwin) {
          const adminTwinUpdates: Partial<typeof twin.adminTwin> = {};

          if (result.fmlFileUrl) {
            adminTwinUpdates.fml_tlasset_url = result.fmlFileUrl;
          }

          if (
            result.metadataPreservation?.attempted &&
            result.metadataPreservation.success &&
            result.metadataPreservation.newSheetUrl
          ) {
            adminTwinUpdates.google_sheet_url = result.metadataPreservation.newSheetUrl;
            addAssetMetadataFeedback({
              intent: 'success',
              message: `Twin Metadata File generated successfully! ${result.metadataPreservation.restoredCount} entities restored from backup.`,
            });
          }

          // Update lifecycle stage
          if (twinBId) {
            try {
              await updateTwin(Config.middlewareApi, accessToken, twinBId, {
                lifecycle_stage: TwinLifecycleStageValues.GraphSaved,
              });
              adminTwinUpdates.lifecycle_stage = TwinLifecycleStageValues.GraphSaved;
            } catch (error) {
              console.error('Failed to update twin lifecycle stage:', describeRequestError(error));
            }
          }

          // Apply all updates in a single setTwin call
          if (Object.keys(adminTwinUpdates).length > 0) {
            setTwin({
              ...twin,
              adminTwin: {
                ...twin.adminTwin,
                ...adminTwinUpdates,
              },
            });
          }

          addImporterFeedback({
            intent: 'success',
            message: 'Lifecycle stage updated to Graph Saved',
          });
        }

        setTwinNeedsRefresh(true);
        setFmlImportFile({
          file: null,
          status: 'unset',
        });
        setImporterMode('READ');
        setExistingTwin(true);
      } else {
        addImporterFeedback({
          intent: 'error',
          message: `Failed to save model to Dgraph: ${result.error || 'Unknown error'}`,
        });
      }
    } catch (error) {
      addImporterFeedback({
        intent: 'error',
        message: `Error saving twin: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsSavingTwin(false);
    }
  }, [
    authMetadata,
    twin,
    lastTwin,
    existingTwin,
    fmlImportFile,
    sensorInputs,
    twinBId,
    addImporterFeedback,
    addAssetMetadataFeedback,
    setTwin,
    setTwinNeedsRefresh,
    setFmlImportFile,
  ]);

  const handleImportModel = () => {
    showDialog({
      title: '',
      content: (
        <>
          <AddFloorplannerFile
            onClose={() => {
              hideDialog();
            }}
            hierarchyString={'zsae'}
            file={fmlImportFile}
            setFile={setFmlImportFile}
            setSensorInputs={setSensorInputs}
            addImporterFeedback={addImporterFeedback}
            onLifecycleStageUpdate={() => {}}
          />
        </>
      ),
    });
  };

  const handleGenerateSheet = () => {
    addAssetMetadataFeedback(
      {
        intent: 'success',
        message: 'Generating Twin Metadata File...',
      },
      true,
    );

    queueMicrotask(async () => {
      await generateMetadataSheet();
    });
  };

  const handleRegenerateSheet = useCallback(async () => {
    const accessToken: string | undefined = authMetadata?.tokens?.accessToken;

    if (!accessToken) {
      addRegenerateSheetFeedback({
        intent: 'error',
        message: 'Missing access token',
      });
      return;
    }

    if (!twinBId) {
      addRegenerateSheetFeedback({
        intent: 'error',
        message: 'Missing twin ID',
      });
      return;
    }

    addRegenerateSheetFeedback(
      {
        intent: 'success',
        message: 'Generating Twin Metadata File...',
      },
      true,
    );

    try {
      const response = await fetch(`${Config.middlewareApi}api/google-sheets/generate-sheet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          twin_bid: twinBId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      addRegenerateSheetFeedback({
        intent: 'success',
        message: `Twin Metadata File generated successfully! ${result.models_count} models processed.`,
      });

      if (twin?.adminTwin) {
        setTwin({
          ...twin,
          adminTwin: {
            ...twin.adminTwin,
            google_sheet_url: result.sheet_url,
          },
        });
      }

      // Open the Google Sheet in a new tab
      window.open(result.sheet_url, '_blank');
    } catch (error) {
      console.error('Error generating Twin Metadata File:', describeRequestError(error));
      addRegenerateSheetFeedback({
        intent: 'error',
        message: 'Failed to generate Twin Metadata File. Please try again.',
      });
    }
  }, [authMetadata, twinBId, addRegenerateSheetFeedback, twin, setTwin]);

  const handleImportSpatialHierarchy = () => {
    addSpatialHierarchyFeedback(
      {
        intent: 'success',
        message: 'Importing spatial hierarchy from Dgraph...',
      },
      true,
    );

    queueMicrotask(async () => {
      await importSpatialHierarchyData();
    });
  };

  const handleImportManufacturerHierarchy = () => {
    addManufacturerHierarchyFeedback(
      {
        intent: 'success',
        message: 'Importing manufacturer hierarchy from Dgraph...',
      },
      true,
    );

    queueMicrotask(async () => {
      await importManufacturerHierarchyData();
    });
  };

  const handleLookupSheet = useCallback(async () => {
    const accessToken: string | undefined = authMetadata?.tokens?.accessToken;

    if (!accessToken) {
      addAssetMetadataFeedback({
        intent: 'error',
        message: 'Missing access token',
      });
      return;
    }

    if (!sheetId) {
      addAssetMetadataFeedback({
        intent: 'error',
        message: 'No sheet available to lookup',
      });
      return;
    }

    addAssetMetadataFeedback(
      {
        intent: 'success',
        message: 'Looking up and updating sheet rows...',
      },
      true,
    );

    try {
      const response = await fetch(`${Config.middlewareApi}api/google-sheets/sync-sheet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ sheetId }),
      });

      if (!response.ok) {
        throw new Error('Sync failed');
      }

      const data = await response.json();
      addAssetMetadataFeedback({
        intent: 'success',
        message: `Sheet updated! ${data.updated} row(s) refreshed.`,
      });
    } catch (error) {
      console.error('Error syncing sheet:', describeRequestError(error));
      addAssetMetadataFeedback({
        intent: 'error',
        message: 'Failed to lookup and update sheet. Please try again.',
      });
    }
  }, [authMetadata, sheetId, addAssetMetadataFeedback]);

  const handleAutoLookupSheet = useCallback(async () => {
    const accessToken: string | undefined = authMetadata?.tokens?.accessToken;

    if (!accessToken) {
      addAssetMetadataFeedback({
        intent: 'error',
        message: 'Missing access token',
      });
      return;
    }

    if (!sheetId) {
      addAssetMetadataFeedback({
        intent: 'error',
        message: 'No sheet available to auto-lookup',
      });
      return;
    }

    addAssetMetadataFeedback(
      {
        intent: 'success',
        message: 'Auto lookup running...',
      },
      true,
    );

    // Start progress polling with cleanup mechanism
    const pollingState = {
      active: true,
      timeoutId: null as ReturnType<typeof setTimeout> | null,
    };

    const cleanupPolling = () => {
      pollingState.active = false;
      if (pollingState.timeoutId) {
        clearTimeout(pollingState.timeoutId);
        pollingState.timeoutId = null;
      }
    };

    try {
      const poller = async () => {
        while (pollingState.active) {
          try {
            const resp = await fetch(
              `${Config.middlewareApi}api/google-sheets/auto-lookup-progress/${sheetId}`,
              {
                headers: { Authorization: `Bearer ${accessToken}` },
              },
            );
            if (resp.ok) {
              const p = await resp.json();
              if (p && typeof p.current === 'number' && typeof p.total === 'number') {
                const cur = p.current;
                const tot = p.total;
                addAssetMetadataFeedback(
                  {
                    intent: 'success',
                    message: `Auto lookup running ${cur}/${tot}...`,
                  },
                  true,
                );
              }
              if (p?.status === 'completed') {
                cleanupPolling();
                break;
              }
            }
          } catch (err) {
            console.error('Error during auto-lookup progress polling:', describeRequestError(err));
          }
          if (pollingState.active) {
            await new Promise<void>(resolve => {
              pollingState.timeoutId = setTimeout(() => {
                resolve();
              }, 1000);
            });
          }
        }
      };
      poller();

      const response = await fetch(`${Config.middlewareApi}api/google-sheets/auto-lookup-sheet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ sheetId }),
      });

      if (!response.ok) {
        cleanupPolling();
        throw new Error('Auto lookup failed');
      }

      const data = await response.json();
      cleanupPolling();
      addAssetMetadataFeedback({
        intent: 'success',
        message: `Auto lookup complete! ${data.updated} row(s) updated.`,
      });
    } catch (error) {
      cleanupPolling();
      console.error('Error auto-lookup sheet:', describeRequestError(error));
      addAssetMetadataFeedback({
        intent: 'error',
        message: 'Failed to auto-lookup sheet. Please try again.',
      });
    }
  }, [authMetadata, sheetId, addAssetMetadataFeedback]);

  const saveMetadataSheet = useCallback(
    async (feedbackCallback?: { add: (feedback: ImporterFeedback, clear?: boolean) => void }) => {
      const addFeedback = feedbackCallback?.add || addAssetMetadataFeedback;
      const accessToken: string | undefined = authMetadata?.tokens?.accessToken;

      if (!accessToken) {
        addFeedback({
          intent: 'error',
          message: 'Missing access token',
        });
        return;
      }

      if (!twinBId) {
        addFeedback({
          intent: 'error',
          message: 'Missing twin ID',
        });
        return;
      }

      const currentSheetId = getSheetId(twin?.adminTwin?.google_sheet_url || undefined);

      if (!currentSheetId) {
        addFeedback({
          intent: 'error',
          message: 'No sheet available to save',
        });
        return;
      }

      setIsSavingSheet(true);
      addFeedback(
        {
          intent: 'success',
          message: 'Saving Twin Metadata File data to DGraph...',
        },
        true,
      );

      try {
        const response = await fetch(`${Config.middlewareApi}api/google-sheets/persist-sheet`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            sheetId: currentSheetId,
            twinBid: twinBId,
          }),
        });

        if (!response.ok) {
          let errorMessage: string;

          switch (response.status) {
            case 400:
              errorMessage = 'Invalid request. Please check your data and try again.';
              break;
            case 401:
              errorMessage = 'Authentication failed. Please log in again.';
              break;
            case 403:
              errorMessage = 'You do not have permission to save this data.';
              break;
            case 404:
              errorMessage = 'Sheet not found. Please regenerate the metadata file.';
              break;
            case 500:
              errorMessage = 'Server error. Please try again later.';
              break;
            default:
              errorMessage = `Failed to save data (${response.status}). Please try again.`;
          }

          throw new Error(errorMessage);
        }

        const result = await response.json();

        if (result.persisted_count === 0 && result.failed_count > 0) {
          addFeedback({
            intent: 'error',
            message: `Failed to persist any entities to DGraph (${result.failed_count} failed)`,
          });
        } else if (result.failed_count > 0) {
          addFeedback({
            intent: 'warning',
            message: `Successfully persisted ${result.persisted_count} entities to DGraph (${result.failed_count} failed)`,
          });
        } else {
          addFeedback({
            intent: 'success',
            message: `Successfully persisted ${result.persisted_count} entities to DGraph`,
          });
        }

        if (result.success && result.persisted_count > 0) {
          setTimeout(() => {
            setTwinNeedsRefresh(true);
          }, 500);
        }
      } catch (error) {
        console.error('Error persisting sheet data:', describeRequestError(error));
        addFeedback({
          intent: 'error',
          message: 'Failed to persist sheet data to DGraph. Please try again.',
        });
      } finally {
        setIsSavingSheet(false);
      }
    },
    [authMetadata, twinBId, addAssetMetadataFeedback, twin, getSheetId, setTwinNeedsRefresh],
  );

  const handleSaveToDgraph6d = useCallback(() => {
    saveMetadataSheet({ add: addSaveToDgraphFeedback6d });
  }, [saveMetadataSheet, addSaveToDgraphFeedback6d]);

  const handleSaveToDgraph6g = useCallback(() => {
    saveMetadataSheet({ add: addSaveToDgraphFeedback6g });
  }, [saveMetadataSheet, addSaveToDgraphFeedback6g]);

  if (!twin) {
    return (
      <div className={className}>
        <div>Loading twin data...</div>
      </div>
    );
  }

  const importModelEnabled = buttonEnabler('import-model');
  const generateSheetEnabled = buttonEnabler('generate-sheet');
  const spatialHierarchyEnabled = buttonEnabler('import-spatial-hierarchy');
  const manufacturerHierarchyEnabled = buttonEnabler('import-manufacturer-hierarchy');

  return (
    <div className={className}>
      <WorkflowContainer>
        <WorkflowSection>
          <StepNumber>1</StepNumber>
          <StepContent>
            <StepTitle>
              Confirm we have the Mandatory documents from the Customer saved in{' '}
              <a
                href="https://twinlabs.sharepoint.com/sites/Everyone/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2FEveryone%2FShared%20Documents%2FCustomers%2FGyms&p=true&ga=1"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#00727D', textDecoration: 'underline' }}
              >
                SharePoint
              </a>
            </StepTitle>
            <StepDescription>
              <SubStep>Gym plan</SubStep>
              <SubStep>Asset Register</SubStep>
            </StepDescription>
          </StepContent>
        </WorkflowSection>

        <WorkflowSection>
          <StepNumber>2</StepNumber>
          <StepContent>
            <StepTitle>
              Follow the steps laid out in{' '}
              <a
                href="https://miro.com/app/board/uXjVLinlQRk=/?moveToWidget=3458764647117659153&cot=14"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#00727D', textDecoration: 'underline' }}
              >
                Miro board
              </a>
            </StepTitle>
          </StepContent>
        </WorkflowSection>

        <WorkflowSection>
          <StepNumber>3</StepNumber>
          <StepContent>
            <StepTitle>Make sure that</StepTitle>
            <StepDescription>
              <SubStep>
                All equipment in the asset register is mapped to a manufacturer Reference ID
              </SubStep>
              <SubStep>A Floorplanner plan has been created and validated</SubStep>
            </StepDescription>
          </StepContent>
        </WorkflowSection>

        <WorkflowSection>
          <StepNumber>4</StepNumber>
          <StepContent>
            <StepTitle>Import FML</StepTitle>
            <StepDescription>Import the Floorplanner Model Layout file</StepDescription>
            {twin?.adminTwin?.fml_tlasset_url && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  width: '100%',
                  marginTop: '0.5rem',
                }}
              >
                <span style={{ fontSize: '14px', color: '#242424', fontWeight: 500 }}>
                  Existing FML:
                </span>
                <Link20Regular style={{ color: '#00727D', flexShrink: 0 }} />
                <div
                  style={{
                    position: 'relative',
                    flex: 1,
                    marginRight: 8,
                    maxWidth: 350,
                    minWidth: 0,
                  }}
                >
                  <HoverTip text={twin.adminTwin.fml_tlasset_url} display="block" maxWidth={340}>
                    <a
                      href={twin.adminTwin.fml_tlasset_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Download FML file"
                      style={{
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: '#00727D',
                        textDecoration: 'underline',
                        fontSize: '14px',
                        maxWidth: '100%',
                      }}
                    >
                      {twin.adminTwin.fml_tlasset_url}
                    </a>
                  </HoverTip>
                </div>
              </div>
            )}
            <StepActions>
              {fmlImportFile.file !== null &&
              fmlImportFile.status !== 'invalid-fml' &&
              fmlImportFile.status !== 'invalid-json' ? (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: 1 }}>
                    <div style={{ fontSize: '14px', color: '#242424', fontWeight: 500 }}>
                      New FML file to save:
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>{fmlImportFile.file.name}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <div style={{ maxWidth: '200px' }}>
                      <Button
                        variant="primary"
                        size="inline"
                        style={{ width: '200px' }}
                        iconLeft={<Save20Regular />}
                        disabled={!importModelEnabled.enabled || isSavingTwin}
                        title="Save FML file to Dgraph"
                        onClick={handleSaveTwin}
                      >
                        Save to Dgraph
                      </Button>
                    </div>
                    <button
                      className="btnDelete"
                      type="button"
                      onClick={() => {
                        setFmlImportFile({
                          file: null,
                          status: 'unset',
                        });
                      }}
                      title="Remove file and upload a different one"
                    >
                      <Delete20Regular />
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ maxWidth: '200px' }}>
                  <Button
                    variant="frosted"
                    size="inline"
                    style={{ width: '200px' }}
                    iconLeft={<InsertTwinIcon />}
                    disabled={!importModelEnabled.enabled}
                    title={!importModelEnabled.enabled ? importModelEnabled.reason : ''}
                    onClick={handleImportModel}
                  >
                    Upload FML File
                  </Button>
                </div>
              )}
            </StepActions>
            {importerFeedback && importerFeedback.length > 0 && (
              <FeedbackContainer>
                {importerFeedback.map((feedback, i) => (
                  <MessageBar key={i} intent={feedback.intent}>
                    {feedback.message}
                    <button
                      onClick={() => removeImporterFeedback(i)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0 4px',
                        fontSize: '18px',
                        lineHeight: '1',
                        color: '#666',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '20px',
                        height: '20px',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.color = '#242424';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.color = '#666';
                      }}
                    >
                      ×
                    </button>
                  </MessageBar>
                ))}
              </FeedbackContainer>
            )}
          </StepContent>
        </WorkflowSection>

        <WorkflowSection>
          <StepNumber>5</StepNumber>
          <StepContent>
            <StepTitle>
              Review Spatial Hierarchy in Asset Panel and resolve in Floorplanner. Only proceed once
              Spatial Hierarchy looks correct.
            </StepTitle>
          </StepContent>
        </WorkflowSection>

        <WorkflowSection>
          <StepNumber>6</StepNumber>
          <StepContent>
            <StepTitle>Generate Google Sheet</StepTitle>
            <StepDescription>
              <span>Generate the Twin Metadata File (Google Sheet) for asset management</span>
              <SubStep>Sort by BID then insert Reference IDs from Asset Register</SubStep>
              <SubStep>Sort by asset name and ensure all names are unique and sensible</SubStep>
            </StepDescription>
            {twin?.adminTwin?.google_sheet_url && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  width: '100%',
                  marginTop: '0.5rem',
                }}
              >
                <span style={{ fontSize: '14px', color: '#242424', fontWeight: 500 }}>
                  Existing Google Sheet:
                </span>
                <Link20Regular style={{ color: '#00727D', flexShrink: 0 }} />
                <div
                  style={{
                    position: 'relative',
                    flex: 1,
                    marginRight: 8,
                    maxWidth: 350,
                    minWidth: 0,
                  }}
                >
                  <HoverTip text={twin.adminTwin.google_sheet_url} display="block" maxWidth={340}>
                    <a
                      href={twin.adminTwin.google_sheet_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open Twin Metadata File in new tab"
                      style={{
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: '#00727D',
                        textDecoration: 'underline',
                        fontSize: '14px',
                        maxWidth: '100%',
                      }}
                    >
                      {twin.adminTwin.google_sheet_url}
                    </a>
                  </HoverTip>
                </div>
              </div>
            )}
            <StepActions>
              <AssetMetadataFileRow
                twin={twin}
                enabled={generateSheetEnabled.enabled}
                reason={generateSheetEnabled.reason}
                onGenerate={handleGenerateSheet}
                feedback={[]}
                addAssetMetadataFeedback={addAssetMetadataFeedback}
                removeAssetMetadataFeedback={removeAssetMetadataFeedback}
                isSavingTwin={isSavingTwin}
                isSavingSheet={isSavingSheet}
              />
            </StepActions>
            <StepActions style={{ marginTop: '0.5rem' }}>
              <div style={{ maxWidth: '200px' }}>
                <Button
                  variant="frosted"
                  size="inline"
                  style={{ width: '200px' }}
                  iconLeft={<TableAdd20Regular />}
                  disabled={!generateSheetEnabled.enabled || isSavingTwin || isSavingSheet}
                  title={
                    isSavingTwin
                      ? 'FML import in progress'
                      : isSavingSheet
                        ? 'Saving to Dgraph in progress'
                        : !generateSheetEnabled.enabled
                          ? generateSheetEnabled.reason
                          : 'Generate Twin metadata file'
                  }
                  onClick={handleGenerateSheet}
                >
                  Generate
                </Button>
              </div>
            </StepActions>
            {assetMetadataFeedback &&
              assetMetadataFeedback.length > 0 &&
              !(
                fmlImportFile.file !== null &&
                fmlImportFile.status !== 'invalid-fml' &&
                fmlImportFile.status !== 'invalid-json'
              ) && (
                <FeedbackContainer>
                  {assetMetadataFeedback.map((feedback, i) => (
                    <MessageBar key={i} intent={feedback.intent}>
                      {feedback.message}
                      <button
                        onClick={() => removeAssetMetadataFeedback(i)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0 4px',
                          fontSize: '18px',
                          lineHeight: '1',
                          color: '#666',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '20px',
                          height: '20px',
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.color = '#242424';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.color = '#666';
                        }}
                      >
                        ×
                      </button>
                    </MessageBar>
                  ))}
                </FeedbackContainer>
              )}
            <SubStepSection>
              <WorkflowSection>
                <SubStepNumber>a</SubStepNumber>
                <StepContent>
                  <StepTitle>Run Manufacturer lookup on Google Sheet</StepTitle>
                  <StepDescription>
                    <SubStep>
                      Search for a manufacturer product below and copy its Reference ID. Paste this
                      ID into the Reference ID column in the Google Sheet, which can then be used
                      for lookup operations.
                    </SubStep>
                  </StepDescription>
                  <StepActions>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                      <div style={{ flex: 1, maxWidth: 600 }}>
                        <ManufacturerProductsAutocomplete placeholder="Search for manufacturer products..." />
                      </div>
                    </div>
                  </StepActions>
                  <StepDescription style={{ marginTop: '0.5rem' }}>
                    <SubStep>
                      <strong>Manufacturers Products:</strong> This is a separate Google Sheet that
                      contains all entries from the manufacturer products view table
                      (v_manufacturer_products). It can be regenerated after new entries have been
                      added.
                    </SubStep>
                  </StepDescription>
                  <StepActions style={{ marginTop: '0.5rem' }}>
                    <AllManufacturerProductsRow
                      enabled={true}
                      reason=""
                      feedback={manufacturerProductsFeedback}
                      addFeedback={addManufacturerProductsFeedback}
                      removeFeedback={removeManufacturerProductsFeedback}
                      manufacturerProductsSheetUrl={manufacturerProductsSheetUrl}
                      onSheetUrlUpdate={setManufacturerProductsSheetUrl}
                    />
                  </StepActions>
                  <StepDescription style={{ marginTop: '0.5rem' }}>
                    <SubStep>
                      <strong>Lookup:</strong> Searches for reference IDs from the Google Sheet in
                      the manufacturer products view table (v_manufacturer_products). If found, it
                      updates the metadata. This only applies to asset entities (areas are ignored).
                    </SubStep>
                    <SubStep>
                      <strong>Auto-Lookup:</strong> Infers reference IDs from the Brand API Name and
                      API Brand. Any existing reference IDs are ignored and will be replaced with
                      newly matched ones. This only applies to asset entities (areas are ignored).
                    </SubStep>
                  </StepDescription>
                  <StepActions
                    style={{
                      marginTop: '0.5rem',
                      display: 'flex',
                      gap: '0.5rem',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ maxWidth: '200px' }}>
                      <Button
                        variant="frosted"
                        size="inline"
                        style={{ width: '200px' }}
                        iconLeft={<Search20Regular />}
                        disabled={!sheetId}
                        title={
                          !sheetId
                            ? 'No file available to lookup'
                            : 'Lookup and update file rows by ID or Floorplanner ID'
                        }
                        onClick={handleLookupSheet}
                      >
                        Lookup
                      </Button>
                    </div>
                    <div style={{ maxWidth: '200px' }}>
                      <Button
                        variant="frosted"
                        size="inline"
                        style={{ width: '200px' }}
                        iconLeft={<ArrowSync20Regular />}
                        disabled={!sheetId}
                        title={
                          !sheetId
                            ? 'No file available to auto-lookup'
                            : 'Auto-lookup Reference IDs by API Name/Brand'
                        }
                        onClick={handleAutoLookupSheet}
                      >
                        Auto-Lookup
                      </Button>
                    </div>
                  </StepActions>
                </StepContent>
              </WorkflowSection>

              <WorkflowSection>
                <SubStepNumber>b</SubStepNumber>
                <StepContent>
                  <StepTitle>Over-ride Google Sheet values directly where required</StepTitle>
                  <StepDescription>
                    <SubStep>3D model</SubStep>
                    <SubStep>Capacity</SubStep>
                  </StepDescription>
                </StepContent>
              </WorkflowSection>

              <WorkflowSection>
                <SubStepNumber>c</SubStepNumber>
                <StepContent>
                  <StepTitle>Validations:</StepTitle>
                  <StepDescription>
                    <SubStep>All Google Sheet rows have unique asset names</SubStep>
                    <SubStep>All Equipment has a Reference ID that has a successful lookup</SubStep>
                  </StepDescription>
                </StepContent>
              </WorkflowSection>

              <WorkflowSection>
                <SubStepNumber>d</SubStepNumber>
                <StepContent>
                  <StepTitle>Save Google Sheet to DGraph</StepTitle>
                  <StepDescription>Save the Google Sheet data to DGraph database</StepDescription>
                  <StepActions>
                    <div style={{ maxWidth: '200px' }}>
                      <Button
                        variant="frosted"
                        size="inline"
                        style={{ width: '200px' }}
                        iconLeft={<Save20Regular />}
                        disabled={isSavingSheet || !sheetId}
                        title={!sheetId ? 'No file available to save' : 'Save file data to DGraph'}
                        onClick={handleSaveToDgraph6d}
                      >
                        Save to DGraph
                      </Button>
                    </div>
                  </StepActions>
                  {saveToDgraphFeedback6d && saveToDgraphFeedback6d.length > 0 && (
                    <FeedbackContainer>
                      {saveToDgraphFeedback6d.map((feedback, i) => (
                        <MessageBar key={i} intent={feedback.intent}>
                          {feedback.message}
                          <button
                            onClick={() => removeSaveToDgraphFeedback6d(i)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0 4px',
                              fontSize: '18px',
                              lineHeight: '1',
                              color: '#666',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: '20px',
                              height: '20px',
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.color = '#242424';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.color = '#666';
                            }}
                          >
                            ×
                          </button>
                        </MessageBar>
                      ))}
                    </FeedbackContainer>
                  )}
                </StepContent>
              </WorkflowSection>

              <WorkflowSection>
                <SubStepNumber>e</SubStepNumber>
                <StepContent>
                  <StepTitle>Regenerate Google Sheet</StepTitle>
                  <StepDescription>
                    Regenerate the Twin Metadata File (Google Sheet) for asset management
                  </StepDescription>
                  <StepActions>
                    <div style={{ maxWidth: '200px' }}>
                      <Button
                        variant="frosted"
                        size="inline"
                        style={{ width: '200px' }}
                        iconLeft={<TableAdd20Regular />}
                        disabled={!generateSheetEnabled.enabled || isSavingTwin || isSavingSheet}
                        title={
                          isSavingTwin
                            ? 'FML import in progress'
                            : isSavingSheet
                              ? 'Saving to Dgraph in progress'
                              : !generateSheetEnabled.enabled
                                ? generateSheetEnabled.reason
                                : 'Generate Twin metadata file'
                        }
                        onClick={handleRegenerateSheet}
                      >
                        Generate
                      </Button>
                    </div>
                  </StepActions>
                  {regenerateSheetFeedback && regenerateSheetFeedback.length > 0 && (
                    <FeedbackContainer>
                      {regenerateSheetFeedback.map((feedback, i) => (
                        <MessageBar key={i} intent={feedback.intent}>
                          {feedback.message}
                          <button
                            onClick={() => removeRegenerateSheetFeedback(i)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0 4px',
                              fontSize: '18px',
                              lineHeight: '1',
                              color: '#666',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: '20px',
                              height: '20px',
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.color = '#242424';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.color = '#666';
                            }}
                          >
                            ×
                          </button>
                        </MessageBar>
                      ))}
                    </FeedbackContainer>
                  )}
                </StepContent>
              </WorkflowSection>

              <WorkflowSection>
                <SubStepNumber>f</SubStepNumber>
                <StepContent>
                  <StepTitle>
                    Adjust rotation of all Equipment in Google Sheet to match photos / Floorplanner
                  </StepTitle>
                </StepContent>
              </WorkflowSection>

              <WorkflowSection>
                <SubStepNumber>g</SubStepNumber>
                <StepContent>
                  <StepTitle>Save Google Sheet to DGraph</StepTitle>
                  <StepDescription>Save the Google Sheet data to DGraph database</StepDescription>
                  <StepActions>
                    <div style={{ maxWidth: '200px' }}>
                      <Button
                        variant="frosted"
                        size="inline"
                        style={{ width: '200px' }}
                        iconLeft={<Save20Regular />}
                        disabled={isSavingSheet || !sheetId}
                        title={!sheetId ? 'No file available to save' : 'Save file data to DGraph'}
                        onClick={handleSaveToDgraph6g}
                      >
                        Save to DGraph
                      </Button>
                    </div>
                  </StepActions>
                  {saveToDgraphFeedback6g && saveToDgraphFeedback6g.length > 0 && (
                    <FeedbackContainer>
                      {saveToDgraphFeedback6g.map((feedback, i) => (
                        <MessageBar key={i} intent={feedback.intent}>
                          {feedback.message}
                          <button
                            onClick={() => removeSaveToDgraphFeedback6g(i)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0 4px',
                              fontSize: '18px',
                              lineHeight: '1',
                              color: '#666',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: '20px',
                              height: '20px',
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.color = '#242424';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.color = '#666';
                            }}
                          >
                            ×
                          </button>
                        </MessageBar>
                      ))}
                    </FeedbackContainer>
                  )}
                </StepContent>
              </WorkflowSection>
            </SubStepSection>
          </StepContent>
        </WorkflowSection>

        <WorkflowSection>
          <StepNumber>7</StepNumber>
          <StepContent>
            <StepTitle>Save Spatial Hierarchy to BI</StepTitle>
            <StepDescription>
              Import spatial hierarchy data from DGraph to the BI database
            </StepDescription>
            <StepActions>
              <div style={{ maxWidth: '200px' }}>
                <Button
                  variant="frosted"
                  size="inline"
                  style={{ width: '300px' }}
                  iconLeft={<TableAdd20Regular />}
                  disabled={!spatialHierarchyEnabled.enabled}
                  title={
                    !spatialHierarchyEnabled.enabled
                      ? spatialHierarchyEnabled.reason
                      : 'Import spatial hierarchy from Dgraph to BI database'
                  }
                  onClick={handleImportSpatialHierarchy}
                >
                  Import Spatial Hierarchy
                </Button>
              </div>
            </StepActions>
            {spatialHierarchyFeedback && spatialHierarchyFeedback.length > 0 && (
              <FeedbackContainer>
                {spatialHierarchyFeedback.map((feedback, i) => (
                  <MessageBar key={i} intent={feedback.intent}>
                    {feedback.message}
                    <button
                      onClick={() => removeSpatialHierarchyFeedback(i)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0 4px',
                        fontSize: '18px',
                        lineHeight: '1',
                        color: '#666',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '20px',
                        height: '20px',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.color = '#242424';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.color = '#666';
                      }}
                    >
                      ×
                    </button>
                  </MessageBar>
                ))}
              </FeedbackContainer>
            )}
          </StepContent>
        </WorkflowSection>

        <WorkflowSection>
          <StepNumber>8</StepNumber>
          <StepContent>
            <StepTitle>Save Manufacturer Reference to BI</StepTitle>
            <StepDescription>
              Import manufacturer hierarchy data from DGraph to the BI database
            </StepDescription>
            <StepActions>
              <div style={{ maxWidth: '200px' }}>
                <Button
                  variant="frosted"
                  size="inline"
                  style={{ width: '300px' }}
                  iconLeft={<TableAdd20Regular />}
                  disabled={!manufacturerHierarchyEnabled.enabled}
                  title={
                    !manufacturerHierarchyEnabled.enabled
                      ? manufacturerHierarchyEnabled.reason
                      : 'Import manufacturer hierarchy from Dgraph to BI database'
                  }
                  onClick={handleImportManufacturerHierarchy}
                >
                  Import Manufacturer Hierarchy
                </Button>
              </div>
            </StepActions>
            {manufacturerHierarchyFeedback && manufacturerHierarchyFeedback.length > 0 && (
              <FeedbackContainer>
                {manufacturerHierarchyFeedback.map((feedback, i) => (
                  <MessageBar key={i} intent={feedback.intent}>
                    {feedback.message}
                    <button
                      onClick={() => removeManufacturerHierarchyFeedback(i)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0 4px',
                        fontSize: '18px',
                        lineHeight: '1',
                        color: '#666',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '20px',
                        height: '20px',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.color = '#242424';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.color = '#666';
                      }}
                    >
                      ×
                    </button>
                  </MessageBar>
                ))}
              </FeedbackContainer>
            )}
          </StepContent>
        </WorkflowSection>
      </WorkflowContainer>
    </div>
  );
};

const WorkflowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 1rem 0;
`;

const WorkflowSection = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;

const StepNumber = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #00bbcc;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StepTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #242424;
`;

const StepDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: #666;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SubStep = styled.span`
  margin-left: 1.5rem;
  display: block;
  font-size: 14px;
  color: #666;
`;

const SubStepSection = styled.div`
  margin-left: 2rem;
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-left: 1rem;
  border-left: 2px solid #e5e7eb;
`;

const SubStepNumber = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #e5e7eb;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  flex-shrink: 0;
`;

const StepActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const FeedbackContainer = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const FMLWorkflow = styled(FMLWorkflow_)`
  .btnPen,
  .btnSave,
  .btnDelete {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .btnPen:hover,
  .btnSave:hover,
  .btnDelete:hover {
    opacity: 0.7;
  }
`;
